import { Component, ElementRef, Input, NgZone, OnDestroy, ViewChild } from '@angular/core';
import { ImageViewerService } from 'ng/src/app/core/image-viewer/image-viewer.service';
import { Table } from 'primeng/table';
import { FileAttachment, FileAttachmentRowData } from './file-attachment.model';
import { PreviewViewerService } from "ng/src/app/core/preview-viewer/preview-viewer.service";
import { ResizeObserverHelper } from "ng/src/app/core/resize-observer-helper";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
	selector: 'lb-file-attachment-list',
	templateUrl: './file-attachment-list.component.html',
	styleUrls: ['./file-attachment.component.scss']
})
export class FileAttachmentListComponent implements OnDestroy {
	@Input() showImageThumbnails = true;
	@Input() responsiveSize = 768;
	
	@Input('type')
	get type(): 'RESPONSIVE' | 'TABLE' | 'CARD' {
		return this._type;
	}
	set type(val) {
		this._type = val;
		if (this._type !== 'RESPONSIVE') this.sizeState = this._type;
		else this.validateResponsiveSize();
	}
	
	@Input() set fileAttachments(files: FileAttachment[]) {
		this.viewModel = FileAttachmentRowData.fromFileAttachments(files);
	}
	
	@ViewChild(Table) table!: Table
	
	viewModel!: FileAttachmentRowData[];
	sizeState: 'TABLE' | 'CARD' = 'TABLE';
	maxCardWidth = 1;
	
	readonly canPreviewType = PreviewViewerService.canPreviewFile;
	readonly getPreviewIcon = PreviewViewerService.getPreviewIcon;
	readonly isImage = FileAttachment.isImage;
	readonly getHref = FileAttachment.getHref;
	readonly downloadFile = FileAttachment.download;
	
	private reassignImageViewerData = true;
	private _type: 'RESPONSIVE' | 'TABLE' | 'CARD' = 'RESPONSIVE';
	private readonly destroy$ = new Subject();
	
	get cardWidth() {
		return `${this.maxCardWidth}px`
	}

	constructor(
		private imageViewerService: ImageViewerService,
		private previewService: PreviewViewerService,
		private ngZone: NgZone,
		private elRef: ElementRef
	) {}
	
	ngOnDestroy(): void {
		this.destroy$.next();
	}
	
	registerObserver() {
		const el = this.elRef.nativeElement?.querySelector('.p-datatable-wrapper');
		if (!el) return;
		
		ResizeObserverHelper.observe(el).pipe(
			takeUntil(this.destroy$)
		)
			.subscribe(result => {
				if (!result) return;
				this.validateResponsiveSize(result.contentRect.width);
			})
	}
	
	validateResponsiveSize(newWidth?: number) {
		if (this.type !== 'RESPONSIVE') return;
		if (newWidth == null) {
			const el = this.elRef.nativeElement?.querySelector('.p-datatable-wrapper');
			if (!el) return;
			newWidth = (el as Element).getBoundingClientRect().width;
		}
		this.sizeState = newWidth > this.responsiveSize ? 'TABLE' : 'CARD';
		this.maxCardWidth = newWidth;
	}

	onSort() {
		this.reassignImageViewerData = true;
	}
	
	openExternalLink(fileAttachment: FileAttachment) {
		window.open(`/#/files?fileId=${fileAttachment.id}`, '_blank');
	}

	onViewImageClicked(imgHref: string) {
		const sortedFileData = this.reassignImageViewerData ?
			this.table.value as FileAttachmentRowData[] :
			null;

		this.imageViewerService.showImageViewer(imgHref, sortedFileData);
		this.reassignImageViewerData = false;
	}
	
	previewFile(file: FileAttachment, event: MouseEvent) {
		if (event?.ctrlKey || event?.metaKey) {
			PreviewViewerService.previewInNewWindow(file);
		}
		else if (this.isImage(file)) {
			this.onViewImageClicked(this.getHref(file))
		}
		else {
			this.previewService.previewFile(file);
		}
	}
}